import React, { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CaseDashboard from '../components/cases/CaseDashboard';
import CaseList from '../components/cases/CaseList';
import CaseDetail from '../components/cases/CaseDetail';
import SpaceBackground from '../components/layout/SpaceBackground';

const Cases = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  // B5 (2026-08-22): the sidebar 'Cases' item is a react-router <Link> to /cases
  // (no full document load, no bundle skew) — but this module keeps the open
  // matter in STATE, so re-clicking the Link changed nothing. Every fresh
  // navigation without a caseId param now returns to the list.
  const location = useLocation();
  useEffect(() => {
    if (!searchParams.get('caseId')) {
      setSelectedCaseId(null);
      setActiveTab((t) => (t === 'detail' ? 'list' : t));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Handle deep link to specific case via URL query param
  useEffect(() => {
    const caseId = searchParams.get('caseId');
    if (caseId) {
      setSelectedCaseId(parseInt(caseId));
      setActiveTab('detail');
      setSearchParams({});
    }
  }, [searchParams]);

  const handleCaseSelect = (caseId) => {
    setSelectedCaseId(caseId);
    setActiveTab('detail');
  };

  const handleBackToList = () => {
    setSelectedCaseId(null);
    setActiveTab('list');
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊' },
    { id: 'list', name: 'Cases', icon: '📋' },
  ];

  return (
    <div className="h-full flex flex-col relative">
      <SpaceBackground />
      {/* Header */}
      <div className="bg-virgo-navy-700/80 backdrop-blur-sm border-b border-virgo-teal-800 px-6 py-4 relative z-10">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-virgo-gold-400">Case Management</h1>
            <p className="text-sm text-virgo-teal-400 mt-1">
              {user?.role === 'admin' ? 'Full Access' : user?.role === 'attorney' ? 'My Cases' : 'My Cases'}
            </p>
          </div>
          {activeTab === 'detail' && (
            <button
              onClick={handleBackToList}
              className="px-4 py-2 text-sm font-medium text-virgo-teal-200 bg-virgo-navy-700 border border-virgo-teal-700 rounded-md hover:bg-virgo-navy-600"
            >
              ← Back to Cases
            </button>
          )}
        </div>

        {/* Tabs */}
        {activeTab !== 'detail' && (
          <div className="mt-4 border-b border-virgo-teal-800">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-virgo-teal-400'
                      : 'border-transparent text-virgo-teal-400 hover:text-virgo-teal-200 hover:border-virgo-teal-700'
                    }
                  `}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto relative z-10">
        {activeTab === 'dashboard' && <CaseDashboard onCaseSelect={handleCaseSelect} />}
        {activeTab === 'list' && <CaseList onCaseSelect={handleCaseSelect} />}
        {activeTab === 'detail' && selectedCaseId && (
          <CaseDetail caseId={selectedCaseId} onBack={handleBackToList} />
        )}
      </div>
    </div>
  );
};

export default Cases;
